<!-- Below the lg breakpoint (992px, including zoomed-in desktops): a native select is easier to use than cramped tabs -->
<div class="form-group d-lg-none">
	<label [for]="categorySelectId" class="small text-muted mb-1">Activity category</label>
	<select class="custom-select"
		[id]="categorySelectId"
		(change)="selectCategoryById($any($event.target).value)">
		<!-- The menu opens on the category the user was already viewing (e.g. the active tab on the
		     Activities page), so the dropdown must show that category on first render. Binding [value] on
		     the <select> can't do that: Angular sets it before *ngFor has created the <option>s, the browser
		     ignores it, and the dropdown falls back to the first option. Binding [selected] on each option
		     runs once the option exists and stays in sync when the category changes. -->
		<option *ngFor="let category of categories" [value]="category.id" [selected]="isCurrentCategory(category)">{{category.label}}</option>
	</select>
</div>

<!-- Large screens and up: category tabs (WAI-ARIA tabs pattern: arrow keys move between tabs, Down arrow jumps into the list) -->
<ul class="nav nav-tabs nav-tabs-transparent activity-category-tabs mb-3 d-none d-lg-flex" role="tablist" aria-label="Activity categories">
	<li *ngFor="let category of categories" class="nav-item" role="presentation">
		<button type="button"
			class="nav-link"
			role="tab"
			[id]="tabId(category)"
			[class.active]="isCurrentCategory(category)"
			[attr.aria-selected]="isCurrentCategory(category)"
			[attr.aria-controls]="panelId(category)"
			[attr.tabindex]="isCurrentCategory(category) ? 0 : -1"
			(click)="selectCategory(category)"
			(keydown)="onTabKeydown($event)"
			#categoryTab>
				{{category.label}}
		</button>
	</li>
</ul>

<div role="tabpanel"
	[id]="panelId(categories.currentItem)"
	[attr.aria-labelledby]="tabId(categories.currentItem)">

	<div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
		<p class="small text-muted mb-1 mr-3">
			Select an activity type to add it. Use <span class="font-weight-bold">Details</span> to read what each type is for and how it is used.
		</p>
		<button type="button"
			class="btn btn-link btn-sm p-0 mb-1"
			[attr.aria-expanded]="allExpanded"
			(click)="toggleAllDescriptions()">
				{{allExpanded ? 'Hide all details' : 'Show all details'}}
		</button>
	</div>

	<!-- Two independent columns (one on small screens). Each column stacks its own cards, so a
	     card that grows when a description is expanded never pushes cards in the other column down. -->
	<div class="row">
		<div *ngFor="let column of typeColumns" class="col-12 col-lg-6">
			<div *ngFor="let type of column" class="card activity-type-card mb-3">
				<div class="card-header d-flex align-items-start">
					<activity-type-icon
						[activityTypeId]="type.id"
						class="activity-type-card-icon text-muted mr-2"></activity-type-icon>
					<div class="flex-grow-1">
						<h2 class="h6 mb-0" [id]="typeHeadingId(type)">{{type.name}}</h2>
						<p *ngIf="definitions.get(type.id)" class="small text-muted mb-0 mt-1">
							{{definitions.get(type.id)}}
						</p>
					</div>
				</div>

				<ul class="list-group list-group-flush" [attr.aria-labelledby]="typeHeadingId(type)">
					<li *ngFor="let st of type.subTypes" class="list-group-item activity-sub-type py-2">
						<div class="d-flex align-items-center">
							<!-- Enabled sub type: selecting it starts a new activity -->
							<button *ngIf="st.allowNewActivities"
								type="button"
								class="btn btn-link text-left flex-grow-1 p-0 activity-sub-type-label activity-sub-type-select"
								(click)="subTypeClick.emit(st)"
								(keydown)="onSubTypeKeydown($event)"
								#subTypeSelect>
									{{st.name}}
							</button>
							<!-- Disabled sub type: shown for context, but cannot be added -->
							<span *ngIf="!st.allowNewActivities" class="activity-sub-type-label activity-sub-type-disabled flex-grow-1 text-muted d-flex align-items-center justify-content-center flex-wrap">
								{{st.name}}
								<span class="badge badge-info border ml-1">Disabled</span>
							</span>

							<button type="button"
								class="btn btn-outline-primary btn-sm ml-2 flex-shrink-0 activity-sub-type-details-toggle"
								[attr.aria-expanded]="isExpanded(st)"
								[attr.aria-controls]="descriptionId(st)"
								[attr.aria-label]="(isExpanded(st) ? 'Hide' : 'Show') + ' details for ' + st.name"
								(click)="toggleDescription(st)">
									<i class="fa"
										[class.fa-chevron-down]="!isExpanded(st)"
										[class.fa-chevron-up]="isExpanded(st)"
										aria-hidden="true"></i>
									<span class="d-none d-sm-inline ml-1">Details</span>
							</button>
						</div>

						<div [id]="descriptionId(st)"
							class="collapse activity-sub-type-description mt-2"
							[class.show]="isExpanded(st)">
							<ng-container *ngIf="isExpanded(st)">
								<ng-container *ngTemplateOutlet="subTypeDescription; context: { $implicit: st }"></ng-container>
							</ng-container>
						</div>
					</li>
				</ul>
			</div>
		</div>
	</div>
</div>

<!-- Descriptions for each activity sub type. Rendered inline under the sub type when its Details toggle is expanded. -->
<ng-template #subTypeDescription let-st>
	<ng-container [ngSwitch]="st.id">
		<ng-container *ngSwitchCase="ActivitySubTypeId.GenericNonAcademic">
			<div class="info-body">
				There is no need to duplicate information already entered by staff/volunteers into other systems:
				<ul>
					<li>4-H Online</li>
					<li>CalFresh System</li>
					<li>California Naturalist VMS</li>
					<li>Master Food Preserver VMS</li>
					<li>Master Gardener VMS</li>
					<li>WebNEERS (EFNEP)</li>
				</ul>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Populates Extension Activity tables in dossier export.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.MeetingOrganized">
			<div class="info-body text-sm">
				<p>
					Classes, short courses, demonstrations, field days, fairs, webinars, Extension meetings, etc. that you organize.
					If you had additional roles besides organizer, note them in the "roles" field.
				</p>
				<p>
					Tip: An Extension Activity is directly related to your own program clientele. If this activity did not serve your clientele,
					 consider putting it in University/Public Service or Evidence of Professional Competence.
				</p>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Populates Extension Activity tables in dossier export.</li>
					<li>Audience demographics included in Civil Rights Compliance analysis.</li>
					<li>Organizational reporting of Extension Activities and Extension Contacts.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.TrainTheTrainer">
			<div class="info-body text-sm">
				<p>
					Trainings you deliver to professionals who then extend information.
					For volunteer capacity building, use another activity type (e.g., Public Service, Meeting Organized).
				</p>
				<p>
					Tip: An Extension Activity is directly related to your own program clientele.
					If this activity did not serve your clientele, consider putting it in University/Public Service or Evidence of Professional Competence.
				</p>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Populates Extension Activity tables in dossier export.</li>
					<li>Audience demographics included in Civil Rights Compliance analysis.</li>
					<li>Organizational reporting of Extension Activities and Extension Contacts.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.EducationalPresentation">
			<div class="info-body text-sm">
				<p>
					Oral presentations and posters that you deliver at meetings organized by someone else.
					If you provided supervision or support for a staff member, use the "Extension Activity Delivered by Your Staff" type.
				</p>
				<p>
					Tip: An Extension Activity is directly related to your own program clientele.
					 If this activity did not serve your clientele, consider putting it in University/Public Service or Evidence of Professional Competence.
				</p>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Populates Extension Activity tables in dossier export.</li>
					<li>Audience demographics included in Civil Rights Compliance analysis.</li>
					<li>Organizational reporting of Extension Activities and Extension Contacts.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.ExternalCollaboration">
			<div class="info-body text-sm">
				<p>
					Extension of knowledge and information via ongoing collaborations.
					Clientele served may be the collaborating organizations/agencies or the individuals reached by the collaboration,
					depending on the nature of your position/program/clientele.
				</p>
				<p>
					Tip: An Extension Activity is directly related to your own program clientele.
					If this activity did not serve your clientele, consider putting it in University/Public Service or Evidence of Professional Competence.
				</p>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Populates Extension Activity tables in dossier export.</li>
					<li>Audience demographics included in Civil Rights Compliance analysis.</li>
					<li>Organizational reporting of Extension Contacts.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.PolicyEngagement">
			<div class="info-body text-sm">
				<p>
					Extension of knowledge and information to clientele during any step in policy development and implementation,
					such as building relationships/leading coalitions;
					equipping decision-makers with the data they need to develop successful policies;
					monitoring and assessing formal decision-making by proper authorities;
					informing policy or regulatory compliance options and actions; etc.
					Report policy briefs and white papers in your non-peer reviewed publications list. Anything listed here should not be duplicated in other activity types or publications.
				</p>
				<p>
					Tip: An Extension Activity is directly related to your own program clientele.
					If this activity did not serve your clientele, consider putting it in University/Public Service or Evidence of Professional Competence.
				</p>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Populates Extension Activity tables in dossier export.</li>
					<li>Audience demographics included in Civil Rights Compliance analysis.</li>
					<li>Organizational reporting of Extension Activities and Extension Contacts.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.OtherExtension">
			<div class="info-body text-sm">
				<p>
					As much as possible, do not use this activity type.
					Try your best to find another activity type that encompasses this activity.
					If you must use this activity type, use it sparingly.
				</p>
				<p>
					Tip: An Extension Activity is directly related to your own program clientele.
					If this activity did not serve your clientele,
					consider putting it in University/Public Service or Evidence of Professional Competence.
				</p>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Populates Extension Activity tables in dossier export.</li>
					<li>Audience demographics included in Civil Rights Compliance analysis.</li>
					<li>Organizational reporting of Extension Contacts.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.IndividualClienteleContacts">
			<div class="info-body text-sm">
				<p>
					Direct extension via technical assistance or one-on-one interactions to your own program clientele where there is a significant educational exchange.
					Consider lumping several individual clientele contacts of one clientele group into one activity record for more efficient reporting and analysis.
				</p>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Does NOT populate Extension Activity tables in the dossier export; instead summarize in your Program Summary Narrative.</li>
					<li>Audience demographics included in Civil Rights Compliance analysis.</li>
					<li>Organizational reporting of Extension Contacts.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.WebsiteOrSocialMedia">
			<div class="info-body text-sm">
				<p>
					Management of specific sites/platforms/accounts you use to extend knowledge and information.
					You may list statistics in the description field.
					You may list the number of posts or blog in the instances field.
					Extensive and substantial posts should be listed in non-peer reviewed publications.
				</p>
				<p>
					Tip: An Extension Activity is directly related to your own program clientele.
					If this activity did not serve your clientele,
					consider putting it in University/Public Service or Evidence of Professional Competence.
				</p>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Populates Extension Activity tables in dossier export.</li>
					<li>Targeted demographic groups included in Civil Rights Compliance analysis.</li>
					<li>Organizational reporting of Extension Activities.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.MediaOutletProgramOrInterview">
			<div class="info-body text-sm">
				<p>
					Radio, television or other mass media outlet programs/interviews in which you deliver significant educational content via a media outlet program or interview.
					Alternatively, if significant educational content was not delivered and the activity was used an invitation to potential clientele,
					consider using the A.R.E. Effort method, "mass media."
				</p>
				<p>
					Tip: An Extension Activity is directly related to your own program clientele. If this activity did not serve your clientele, consider putting it in University/Public Service or Evidence of Professional Competence.
				</p>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Populates Extension Activity tables in dossier export.</li>
					<li>Targeted demographic groups included in Civil Rights Compliance analysis.</li>
					<li>Organizational reporting of Extension Activities.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.Research">
			<div class="info-body text-sm">
				<p>
					Basic and applied research, program evaluation, and needs assessment activities.							
				</p>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Does NOT populate Extension Activity tables in dossier export; instead, summarize in your Program Summary Narrative.</li>
					<li>NOT included in Civil Rights Compliance analysis.</li>
					<li>Organizational reporting of Extension Activities.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.DigitalMedia">
			<div class="info-body text-sm">
				<p>
					This activity type has been removed. Per the E-Book, Digital Media should actually go in your bibliography.
				</p>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.EducationalMaterials">
			<div class="info-body text-sm">
				<p>
					This activity type has been removed. Per the E-Book, Educational Materials should actually go in your bibliography.
				</p>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.OtherAppliedResearchOrCreative">
			<div class="info-body text-sm">
				<p>
					As much as possible, do not use this activity type.
					Try your best to find another activity type that encompasses this activity.
					If you must use this activity type, use it sparingly.
				</p>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Does NOT populate Extension Activity tables in dossier export; instead, summarize in your Program Summary Narrative.</li>
					<li>NOT included in Civil Rights Compliance analysis.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.IndividualOutreach">
			<div class="info-body text-sm">
				<p [innerHTML]="tooltipSvc.tooltip('AddActivity.IndividualEffort')"></p>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.MassMediaOutreach">
			<div class="info-body text-sm">
				<p>
					Mass media to invite potential clientele to participate in programs.
					Examples include press releases, public service announcements, radio and/or television appearances, social media,
					and other web-based avenues. Can be in electronic and print outlets.
					Consider lumping several mass media efforts related to one clientele group into one activity record for more efficient reporting and analysis.
					If this activity included delivery of significant educational content,
					consider using one of the "Extension and/or A.R.E. Effort" activity types so that it will be included in both dossier tables and Civil Rights Compliance analysis. 
				</p>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Does NOT populate Extension Activity tables in dossier export; instead, summarize in your Program Summary Narrative.</li>
					<li>
						Included in Civil Rights Compliance analysis.
					</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.NewsletterOutreach"> <!-- now Promotional Material Effort -->
				<div class="info-body text-sm">
					<p>
						Promotional material to invite potential clientele to participate in programs. Examples include newsletters, posters, and flyers 
						distributed in a "mass mailing" type of process. Can be in hardcopy or electronic format. Consider lumping several promotion materials 
						efforts related to one clientele group into one activity record for more efficient reporting and analysis. Newsletters with significant 
						educational content should be reported in non-peer reviewed publications. 
					</p>
					<p>
						How this information will be used:
					</p>
					<ul>
						<li>Does NOT populate Extension Activity tables in dossier export; instead, summarize in your Program Summary Narrative.</li>
						<li>
							Included in Civil Rights Compliance analysis.
						</li>
					</ul>
				</div>
			</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.PersonalLetterOutreach">
			<div class="info-body text-sm">
				<p>
					Personal letters to invite individuals from potential clientele groups to participate in programs,
					informing them of dates and times of program activities and specific invitations for them to attend and participate.
					Can be in hardcopy or electronic format.
					Consider lumping several personal letter efforts related to one clientele group into one activity record for more efficient reporting and analysis.
				</p>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Does NOT populate Extension Activity tables in dossier export; instead, summarize in your Program Summary Narrative.</li>
					<li>
						Included in Civil Rights Compliance analysis.
					</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.OtherOutreach">
			<div class="info-body text-sm">
				<p [innerHTML]="tooltipSvc.tooltip('AddActivity.OtherARE')"></p>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.ExtensionByYourStaff"><!-- ????? -->
			<div class="info-body text-sm">
				<p>
					Efforts in supporting the Extension Activities of your staff/volunteers.
					Consider lumping several activities into one activity record for more efficient reporting and analysis. 
				</p>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Populates a separate table in your dossier export, per E-Book policy.</li>
					<li>Audience demographics included in Civil Rights Compliance analysis.</li>
					<li>Organizational reporting of Extension Activities and Extension Contacts.</li>
				</ul>
				<p>
					Special Instructions for the following Statewide Programs: UC 4-H, UC CalFresh Nutrition Education, California Naturalist, EFNEP, UC Master Food Preserver, UC Master Gardener:
					There is no need to duplicate participant demographics of Extension Activities delivered by staff/volunteers/partners if those demographics are already being entered in other Statewide Program online reporting systems (4hOnline, PEARS, WEBNEERS, VMS). Civil Rights Compliance analysis and organizational reporting will utilize data directly from those Statewide Programs' systems!
				</p>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.UniversityService">
			<div class="info-body text-sm">
				<p>
					University service may occur at the local, division, state, national, or international level. 
				</p>
				<p>Examples:</p>
				<ul>
					<li>Advocacy efforts</li>
					<li>Committee service (which years)</li>
					<li>Workgroup chair, treasurer, secretary, etc.</li>
					<li>Leadership in strategic initiative activities and program teams</li>
				</ul>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Populates University and Public Service tables in dossier export.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.PublicService">
			<div class="info-body text-sm">
				<p>
					Public service should involve activities and events in which professional expertise is used to benefit groups or efforts outside the University.
				</p>
				<p>Examples:</p>
				<ul>
					<li>Serving on external boards, commissions or councils</li>
					<li>Participating in community events or fairs</li>
					<li>Leadership of non-University collaborative groups, councils</li>
				</ul>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Populates University and Public Service tables in dossier export.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.ProfessionalDevelopmentAndTraining">
			<div class="info-body text-sm">
				<p>Training activity examples:</p>
				<ul>
					<li>Workgroup and non-workgroup training activities</li>
					<li>Attendance at conferences, symposia and workshops</li>
					<li>Administrative or technology trainings</li>
				</ul>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Populates Professional Competence tables in dossier export.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.EvidenceOfProfessionalCompetence">
			<div class="info-body text-sm">
				<p>Activities that reflect your professional standing.</p>
				<p>Examples:</p>
				<ul>
					<li>Conferences, meetings/trainings that you organized for professionals or colleagues (including workgroups)</li>
					<li>Professional society presentations and/or offices held</li>
					<li>Presentations you were invited to give due to your professional competence</li>
					<li>Books or journals edited, articles reviewed or refereed</li>
					<li>Webinars developed for statewide and/or nationwide peers</li>
					<li>Sabbatical/special leaves</li>
				</ul>
				<p>
					How this information will be used:
				</p>
				<ul>
					<li>Populates Professional Competence tables in dossier export.</li>
				</ul>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.NetworkingCollaborationWithOrgs">
			<div class="info-body text-sm">
				<p [innerHTML]="tooltipSvc.tooltip('AddActivity.NetworkingCollaborationWithOrgs')"></p>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.AcademicProgramPlanningPartnershipDev">
			<div class="info-body text-sm">
				<p [innerHTML]="tooltipSvc.tooltip('AddActivity.AcademicProgramPlanningPartnershipDev')"></p>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.ProgramAssessmentEvaluation">
			<div class="info-body text-sm">
				<p [innerHTML]="tooltipSvc.tooltip('AddActivity.ProgramAssessmentEvaluation')"></p>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.ExtramuralFundingIdOfSupportResources">
			<div class="info-body text-sm">
				<p [innerHTML]="tooltipSvc.tooltip('AddActivity.ExtramuralFundingIdOfSupportResources')"></p>
			</div>
		</ng-container>
		<ng-container *ngSwitchCase="ActivitySubTypeId.SupervisionLeadershipOfAcademicOrStaff">
			<div class="info-body text-sm">
				<p [innerHTML]="tooltipSvc.tooltip('AddActivity.SupervisionLeadershipOfAcademicOrStaff')"></p>
			</div>
		</ng-container>
		<ng-container *ngSwitchDefault>
			<div class="info-body text-sm">
				<p class="mb-0">No additional details are available for this activity type.</p>
			</div>
		</ng-container>
	</ng-container>
</ng-template>
